import React from 'react';
import { OFFICIELLE_FLAGDAGE } from '../constants/officialFlagDays';
import { beregnPaaskedag } from '../utils/dateUtils';
import { DANSKE_MAANEDER } from '../utils/dateUtils';
import styles from './OmSiden.module.scss';

function getFastDatoTekst(maaned?: number, dag?: number): string {
  if (!maaned || !dag) return '';
  return `${dag}. ${DANSKE_MAANEDER[maaned - 1].toLowerCase()}`;
}

function getPaskeDatoTekst(paskeOffset?: number): string {
  if (paskeOffset === undefined) return '';
  const currentYear = new Date().getFullYear();
  const paaskedag = beregnPaaskedag(currentYear);
  const dato = new Date(paaskedag);
  dato.setDate(dato.getDate() + paskeOffset);
  const dag = dato.getDate();
  const maaned = DANSKE_MAANEDER[dato.getMonth()].toLowerCase();
  if (paskeOffset === 0) return `${dag}. ${maaned} (påske)`;
  if (paskeOffset > 0) return `${dag}. ${maaned} (påske + ${paskeOffset} dage)`;
  return `${dag}. ${maaned} (påske − ${Math.abs(paskeOffset)} dage)`;
}

const OmSiden: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const fasteDage = OFFICIELLE_FLAGDAGE.filter((f) => f.beregning === 'fast');
  const paeskeDage = OFFICIELLE_FLAGDAGE.filter((f) => f.beregning === 'paske_offset');

  return (
    <div className={styles.page}>
      <div className={`${styles.content} container`}>
        <h1 className={styles.pageTitle}>Om Askevej Flagdage</h1>

        {/* Hvad er flagdage */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Hvad er flagdage?</h2>
          <div className={styles.card}>
            <p>
              I Danmark er der tradition for at hejse Dannebrog – det røde flag
              med det hvide kors – på særlige dage. Der er officielle flagdage
              fastsat af Staten, samt lokale traditioner for at flage ved
              fødselsdage og andre mærkedage.
            </p>
            <p>
              Dannebrog er et af verdens ældste nationalflag og er ifølge
              legenden faldet ned fra himlen under Valdemarsslaget den 15. juni
              1219 i Estland.
            </p>
            <p>
              Det er god skik at hejse flaget ved solopgang og hale det ned ved
              solnedgang. Flaget må ikke hejses i mørke.
            </p>
          </div>
        </section>

        {/* Officielle flagdage tabel */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Officielle danske flagdage {currentYear}</h2>

          <div className={styles.tableCard}>
            <h3 className={styles.tableSubtitle}>Faste datoer</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Dato</th>
                  <th>Dag</th>
                  <th>Beskrivelse</th>
                </tr>
              </thead>
              <tbody>
                {fasteDage.map((dag) => (
                  <tr key={dag.id}>
                    <td className={styles.dateCell}>
                      {getFastDatoTekst(dag.maaned, dag.dag)}
                    </td>
                    <td className={styles.titleCell}>{dag.titel}</td>
                    <td className={styles.descCell}>{dag.beskrivelse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.tableCard}>
            <h3 className={styles.tableSubtitle}>Påskebaserede dage ({currentYear})</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Dato</th>
                  <th>Dag</th>
                  <th>Beskrivelse</th>
                </tr>
              </thead>
              <tbody>
                {paeskeDage.map((dag) => (
                  <tr key={dag.id}>
                    <td className={styles.dateCell}>
                      {getPaskeDatoTekst(dag.paskeOffset)}
                    </td>
                    <td className={styles.titleCell}>{dag.titel}</td>
                    <td className={styles.descCell}>{dag.beskrivelse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Hvad er Askevej */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Hvad er Askevej?</h2>
          <div className={styles.card}>
            <p>
              Askevej er en vej i Danmark. Denne hjemmeside er lavet til beboerne
              på og omkring Askevej, så vi nemt kan holde styr på flagdage,
              fødselsdage og mærkedage i nabolaget.
            </p>
            <p>
              Er du nabo på Askevej? Tilføj din fødselsdag eller en særlig
              mærkedag, og tilmeld dig email-notifikationer, så du aldrig
              glemmer at hejse flaget!
            </p>
          </div>
        </section>

        {/* Sådan bruger du siden */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Sådan bruger du siden</h2>
          <div className={styles.card}>
            <ul className={styles.howToList}>
              <li>
                <span className={styles.stepIcon}>🏠</span>
                <div>
                  <strong>Forside</strong> – Se de næste kommende flag- og
                  mærkedage samt en oversigt over denne måneds begivenheder.
                </div>
              </li>
              <li>
                <span className={styles.stepIcon}>📅</span>
                <div>
                  <strong>Kalender</strong> – Gennemse alle begivenheder måned
                  for måned. Tryk på en dag for at se detaljer.
                </div>
              </li>
              <li>
                <span className={styles.stepIcon}>➕</span>
                <div>
                  <strong>Tilføj dag</strong> – Tilføj en fødselsdag eller
                  mærkedag som er vigtig for Askevej.
                </div>
              </li>
              <li>
                <span className={styles.stepIcon}>🔔</span>
                <div>
                  <strong>Notifikationer</strong> – Tilmeld din email og modtag
                  en påmindelse inden vigtige dage.
                </div>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OmSiden;
